import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsUserLogged } from '@selectors/auth';
import { addProductToBasket } from '@actions/basket';
import { selectShopEventStatus } from '@selectors/shopEvent';

import { measureAddToBasket } from '@utils/analytics';

import { SHOP_EVENT_AVAILABILITY_LABEL } from '@common/constants/shopEvent';
import { PRODUCT_TYPE } from '@common/constants/products';
import {
  productAllTypesColorVariationShape,
  productAllTypesShape,
} from '@common/types/product';
import { useStickyBarVisibilityContext } from '@common/contexts/stickyBarVisibilityContext';
import { useStickyElement } from '@common/hooks/useStickyElement';
import { useElementScroll } from '@common/hooks/useElementScroll';
import DescriptionAccordion from '@common/components/ProductComponents/Content/DescriptionAccordion';

import Sizes from './Sizes';
import ColorVariations from './ColorVariations';
import Pills from './Pills';
import StoreAvailability from './StoreAvailability';
import Pricing from './Pricing';
import AvailabilityLabel from './AvailabilityLabel';
import AddToBasketForm from './AddToBasketForm';
import {
  ArticleNumber,
  ContentWrapper,
  Title,
  ContentContainer,
} from './Content.styled';

const Content = ({ product, colorVariation, sizeId, productType }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isUserLogged = useSelector(selectIsUserLogged);
  const { shopEventId } = useSelector(selectShopEventStatus);
  const isStickyBarVisible = useStickyBarVisibilityContext();
  const { categories, colorVariations, title } = product;

  const {
    productColorId,
    colorTitle,
    colorhex,
    offerType,
    price,
    publishedUntil,
    sizes = [],
    slug,
    image,
    specialPrice,
    vendorProductColorId,
    productId,
    availabilityLabel = SHOP_EVENT_AVAILABILITY_LABEL.HIGH,
  } = colorVariation;

  const articleNr = sizeId && `${productColorId}-${sizeId}`;
  const selectedItem =
    articleNr && sizes.find(size => size?.sanitizedArticleNr === articleNr);
  const hasLabel =
    productType === PRODUCT_TYPE.SHOP_EVENT &&
    availabilityLabel !== SHOP_EVENT_AVAILABILITY_LABEL.HIGH;
  const stickyContentRef = useRef(null);
  const stickyContentWrapperRef = useRef(null);
  const { shouldAnimate } = useStickyElement(stickyContentRef);
  const { isElementScrolled, isScrollingDown } = useElementScroll(
    stickyContentWrapperRef,
  );

  const handleAddToBasket = quantity => {
    measureAddToBasket(
      shopEventId,
      Number(((specialPrice || price) * quantity).toFixed(2)),
      {
        color_id: productColorId,
        id: productId,
        name: title,
        price: Number(specialPrice || price),
        productId,
        size: selectedItem.title,
        variant: colorTitle,
        quantity,
        affiliation: 'Online',
        specialPrice,
        ordinaryPrice: price,
      },
    );
    dispatch(
      addProductToBasket({
        shopEventId,
        product: {
          articleNr: selectedItem.articleNr,
          sanitizedArticleNr: articleNr,
          quantity,
          title,
          colorTitle,
          colorhex,
          slug,
          image,
          price,
          specialPrice,
          availability: selectedItem.availability,
          size: selectedItem.title,
        },
      }),
    );
  };

  return (
    <ContentWrapper ref={stickyContentWrapperRef}>
      <ContentContainer
        isStickyBarVisible={isStickyBarVisible}
        shouldAnimate={shouldAnimate}
        ref={stickyContentRef}
        isScrollingDown={isScrollingDown}
        isScrolled={isElementScrolled}
        hasLabel={hasLabel}
      >
        {hasLabel && (
          <AvailabilityLabel
            productType={productType}
            label={availabilityLabel}
          />
        )}

        {productType === PRODUCT_TYPE.DEFAULT && (
          <>
            <Pills
              categories={categories}
              productColorId={productColorId}
              offerType={offerType}
            />
            <StoreAvailability
              isUserLogged={isUserLogged}
              slug={slug}
              sizeId={sizeId}
              productColorId={productColorId}
            />
          </>
        )}

        <Title
          data-testid="productTitle"
          variant="h5"
        >
          {title}
        </Title>

        <Pricing
          price={price}
          offerType={offerType}
          specialPrice={specialPrice}
        />

        {(selectedItem?.itemNr || vendorProductColorId) && (
          <ArticleNumber>
            {t(
              'productDetails.articleNumber',
              'Artikelnummer {{articleNumber}}',
              {
                articleNumber: selectedItem?.itemNr || vendorProductColorId,
              },
            )}
          </ArticleNumber>
        )}

        <ColorVariations
          colorVariations={colorVariations}
          productColorId={productColorId}
          colorTitle={colorTitle}
          productType={productType}
        />

        {!!sizes.length && (
          <Sizes
            sizes={sizes}
            articleNr={articleNr}
            publishedUntil={publishedUntil}
            slug={slug}
            productType={productType}
          />
        )}

        <DescriptionAccordion product={product} />

        {productType === PRODUCT_TYPE.SHOP_EVENT && (
          <AddToBasketForm
            key={selectedItem?.articleNr}
            articleNr={selectedItem?.articleNr}
            availability={selectedItem?.availability}
            onAddToBasket={handleAddToBasket}
          />
        )}
      </ContentContainer>
    </ContentWrapper>
  );
};

Content.propTypes = {
  product: productAllTypesShape.isRequired,
  colorVariation: productAllTypesColorVariationShape.isRequired,
  sizeId: PropTypes.string,
  productType: PropTypes.oneOf([PRODUCT_TYPE.DEFAULT, PRODUCT_TYPE.SHOP_EVENT]),
};

Content.defaultProps = {
  sizeId: undefined,
  productType: PRODUCT_TYPE.DEFAULT,
};

export default Content;
